import {
  json,
  type HeadersFunction,
  type LoaderFunctionArgs,
  type ActionFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { Link, useLoaderData, useSubmit } from "@remix-run/react";
import * as React from "react";
import { useFetcher } from "react-router-dom";
import ArrowButton from "#app/components/arrowButton.tsx";
import BackgroundImage from "#app/components/backgroundImage.tsx";
import CallToActionCard from "#app/components/callToAction.tsx";
import Header from "#app/components/header.tsx";
import { Button } from "#app/components/ui/button.tsx";
import { Checkbox } from "#app/components/ui/checkbox.tsx";
import { CRAFT_QUERY_KEYS, getPageDetails } from "#app/utils/graphql.server.ts";
import { seomaticMeta } from "#app/utils/misc";
import { type NewsListItem } from "../../../types/graphql.page.ts";

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const data = await getPageDetails(request, "news");
  // const page = Number(formData.get("page")) || 0;
  const sectors =
    Number(formData.get("sectors")?.toString()?.trim()?.length) > 0
      ? formData.get("sectors")?.toString().trim().split(",")
      : [];
  const companies =
    Number(formData.get("companies")?.toString()?.trim()?.length) > 0
      ? formData.get("companies")?.toString().trim().split(",")
      : [];

  const newsList =
    data.newsList
      .filter((nl) => {
        if (sectors?.length && companies?.length) {
          return (
            sectors.every((s) => nl.newsIndustrySector.includes(s)) &&
            companies.every((c) =>
              nl.newsPortfolio.map((p) => p.title).includes(c)
            )
          );
        } else if (sectors?.length) {
          return sectors.every((s) => nl.newsIndustrySector.includes(s));
        } else if (companies?.length) {
          return companies.every((c) =>
            nl.newsPortfolio.map((p) => p.title).includes(c)
          );
        } else {
          return true;
        }
      })
      // .slice(
      // 	page * 4,
      // 	page * 4 + 4
      // )
      .map((nl) => ({
        ...nl,
        newsContent:
          nl.newsSnippet ||
          nl.newsContent.replace(/(<([^>]+)>)/gi, "").split(".")[0] + ".",
      })) || [];

  return json({
    sectors: sectors,
    companies: companies,
    newsList,
  });
}

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const companies = url.searchParams
    .get("companies")
    ?.split(",")
    .filter(Boolean);
  const sectors = url.searchParams.get("sectors")?.split(",").filter(Boolean);
  const noCache = Object.entries(Object.fromEntries(url.searchParams)).filter(
    ([key]) => CRAFT_QUERY_KEYS.includes(key)
  ).length;
  const data = await getPageDetails(request, "news");

  return json(
    {
      selected: {
        company: companies || [],
        sector: sectors || [],
      },
      portfolioList: data.portfolioList.map((p) => p.title) || [],
      sectors: [
        { name: "Transportation", value: "transportation" },
        { name: "Energy", value: "energy" },
        { name: "Construction", value: "construction" },
        {
          name: "Exploration and Production",
          value: "explorationAndProduction",
        },
        { name: "Real Estate", value: "realEstate" },
      ],
      seo: data?.seo,
      portfoliosAvailable:
        data.newsList
          .map((nl) => nl.newsPortfolio.map((p) => p.title))
          .flat()
          // filter unique
          .filter((value, index, self) => self.indexOf(value) === index) || [],
      sectorsAvailable:
        data.newsList
          .map((nl) => nl.newsIndustrySector)
          .flat()
          // filter unique
          .filter((value, index, self) => self.indexOf(value) === index) || [],
      newsList:
        data.newsList
          .filter((nl) => {
            if (sectors?.length && companies?.length) {
              return (
                sectors.every((s) => nl.newsIndustrySector.includes(s)) &&
                companies.every((c) =>
                  nl.newsPortfolio.map((p) => p.title).includes(c)
                )
              );
            } else if (sectors?.length) {
              return sectors.every((s) => nl.newsIndustrySector.includes(s));
            } else if (companies?.length) {
              return companies.every((c) =>
                nl.newsPortfolio.map((p) => p.title).includes(c)
              );
            } else {
              return true;
            }

            // if (company) {
            //   return nl.newsPortfolio.some((p) => {
            //     return p.title === company?;
            //   });
            // } else {
            //   return true;
            // }
          })
          .map((nl) => ({
            ...nl,
            newsContent:
              nl.newsSnippet ||
              nl.newsContent.replace(/(<([^>]+)>)/gi, "").split(".")[0] + ".",
          })) || [],
      subTitle: data.entry?.subtitle || "",
      title: data.entry?.title || "News",
      footer: data.entry?.footer && {
        ...data.entry.footer[0],
        enabled: data.entry.footerEnabled,
      },
    },
    {
      headers: {
        "Cache-Control": noCache
          ? "max-age=0, s-maxage=0"
          : "max-age=60, s-maxage=300",
      },
    }
  );
}

export let headers: HeadersFunction = ({ loaderHeaders }) => {
  return { "Cache-Control": loaderHeaders.get("Cache-Control") || "" };
};

export const meta: MetaFunction<
  typeof loader
> = ({ data }: {data: any}) => {
  return seomaticMeta(data.seo)
}
export default function NewsRoute() {
  const fetcher = useFetcher();
  const submit = useSubmit();
  const {
    newsList,
    sectorsAvailable,
    portfoliosAvailable,
    sectors,
    portfolioList,
    title,
    subTitle,
    footer,
    selected,
  } = useLoaderData<typeof loader>();
  const [page, setPage] = React.useState(0);
  const [currentPageRange, setCurrentPageRange] = React.useState({
    start: 1,
    end: newsList.length < 4 ? newsList.length : 4,
  });
  const [hasNextPage, setHasNextPage] = React.useState(newsList.length > 4);
  const [hasPrevPage, setHasPrevPage] = React.useState(false);
  const [portfolioLimited, setPortfolioLimited] = React.useState<string[]>(
    selected.company
  );
  const [industryLimited, setIndustryLimited] = React.useState<string[]>(
    selected.sector
  );
  const [currentNewsList, setCurrentNewsList] = React.useState(newsList);
  console.log(newsList);

  React.useEffect(() => {
    setCurrentNewsList(newsList);
  }, [newsList]);

  function nextPage() {
    if (hasNextPage) {
      setPage(page + 1);
      setCurrentPageRange({
        start: currentPageRange.start + 4,
        end:
          currentPageRange.end + 4 > newsList.length
            ? newsList.length
            : currentPageRange.end + 4,
      });
      setHasNextPage(page + 1 < newsList.length / 4 - 1);
      setHasPrevPage(true);
      setCurrentNewsList(newsList.slice(page * 4 + 4, page * 4 + 8));
    }
  }

  function prevPage() {
    if (hasPrevPage) {
      setPage(page - 1);
      setCurrentPageRange({
        start: currentPageRange.start - 4 < 1 ? 1 : currentPageRange.start - 4,
        end: currentPageRange.end - 4 < 4 ? 4 : currentPageRange.end - 4,
      });
      setHasPrevPage(page - 1 > 0);
      setHasNextPage(true);
      setCurrentNewsList(newsList.slice(page * 4 - 4, page * 4));
    }
  }

  function updateNewsList({
    type,
    value,
  }: {
    type: "company" | "sector";
    value: string;
  }) {
    let currentIndustryLimited = industryLimited;
    let currentPortfolioLimited = portfolioLimited;
    switch (type) {
      case "sector": {
        if (industryLimited.includes(value)) {
          currentIndustryLimited = industryLimited.filter((l) => l !== value);
        } else {
          currentIndustryLimited = [...industryLimited, value];
        }
        setIndustryLimited(currentIndustryLimited);
        break;
      }
      case "company": {
        if (portfolioLimited.includes(value)) {
          currentPortfolioLimited = portfolioLimited.filter((p) => p !== value);
        } else {
          currentPortfolioLimited = [...portfolioLimited, value];
        }
        setPortfolioLimited(currentPortfolioLimited);
        break;
      }
    }

    submit(
      {
        companies: currentPortfolioLimited,
        sectors: currentIndustryLimited,
      },
      {
        method: "GET",
        preventScrollReset: true,
      }
    );
  }

  React.useEffect(() => {
    if (fetcher.state === "loading" && fetcher.data?.newsList) {
      setPage(0);
      setCurrentPageRange({
        start: 1,
        end:
          fetcher.data?.newsList.length < 4 ? fetcher.data?.newsList.length : 4,
      });
      setCurrentNewsList(fetcher.data?.newsList);
    }
  }, [fetcher.data, fetcher.state]);

  return (
    <>
      <Header subtitle={subTitle} title={title} />
      <main className="container flex flex-col place-items-start gap-10 py-20 md:grid md:grid-cols-12">
        <div className="col-span-3">
          <h3 className="mb-9 font-sans-serif text-2xl font-light leading-9">
            Filter by
          </h3>
          <div className="mb-10">
            <h4 className="mb-5 font-sans-serif text-base font-semibold leading-6">
              Industry Sector
            </h4>
            <div className="flex flex-col gap-4">
              {sectors.map(({ name, value }) => (
                <div key={value} className="flex items-center space-x-4 py-1">
                  <Checkbox
                    defaultChecked={selected?.sector?.includes(value)}
                    disabled={!sectorsAvailable.includes(value)}
                    onClick={() => updateNewsList({ type: "sector", value })}
                    className="border-gray-500"
                    id={`sector-${value}`}
                  />
                  <label
                    htmlFor={`sector-${value}`}
                    className="font-sans-serif text-base font-light leading-none text-green-100 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {name}
                  </label>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="mb-5 font-sans-serif text-base font-semibold leading-6">
              Company
            </h4>
            <div className="flex flex-col gap-4">
              {portfolioList.map((company) => (
                <div key={company} className="flex items-center space-x-4 py-1">
                  <Checkbox
                    defaultChecked={selected?.company?.includes(company)}
                    disabled={!portfoliosAvailable.includes(company)}
                    onClick={() =>
                      updateNewsList({ type: "company", value: company })
                    }
                    className="border-gray-500"
                    id={`company-${company}`}
                  />
                  <label
                    htmlFor={`company-${company}`}
                    className="font-sans-serif text-base font-light leading-none text-green-100 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {company}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="col-span-9 flex flex-col gap-10">
          {currentNewsList.length > 0 ? (
            currentNewsList?.map((entry: NewsListItem | any) => (
              <Link
                to={`/${entry.uri}`}
                key={entry.uid}
                className="group flex flex-col md:flex-row items-center space-x-4"
              >
                <div className="h-[100px] w-full sm:h-[150px] sm:w-full md:h-[200px] md:w-[200px] lg:h-[400px] lg:w-[400px]">
                  {entry.newsListingImage?.at(0)?.url ? (
                    <BackgroundImage
                      className="h-[100px] w-full sm:h-[150px] sm:w-full md:h-[200px] md:w-[200px] lg:h-[400px] lg:w-[400px] bg-cover "
                      img={entry.newsListingImage[0]}
                    />
                  ) : (
                    <img
                      className=""
                      src="/assets/news-placeholder.png"
                      alt="News"
                      title="News"
                    />
                  )}
                </div>
                <div className="flex flex-col space-y-4">
                  <h4 className="font-sans-serif text-2xl font-light leading-9 text-green-300">
                    {entry.title}
                  </h4>
                  <p className="font-sans-serif text-lg font-light leading-7 text-gray-100">
                    {/* let strippedString = originalString.replace(/(<([^>]+)>)/gi, ""); */}
                    {entry.newsSnippet.replace(/(<([^>]+)>)/gi, "")}
                  </p>
                  <ArrowButton className="text-green-200">
                    Read More
                  </ArrowButton>
                </div>
              </Link>
            ))
          ) : industryLimited.length > 0 || portfolioLimited.length > 0 ? (
            <h4 className="pb-3 leading-10">
              There are no news articles with your selected filters.
            </h4>
          ) : (
            <h4 className="pb-3 leading-10">
              There are no news articles at this time.
            </h4>
          )}
        </div>
        <div className="col-span-full flex w-full flex-col items-center">
          <div className="">
            <Button
              onClick={() => prevPage()}
              className={`font-light text-copper-100`}
              disabled={!hasPrevPage}
            >
              &lt; Prev
            </Button>
            <Button
              onClick={() => nextPage()}
              className={`font-light text-copper-100`}
              disabled={!hasNextPage}
            >
              Next &gt;
            </Button>
          </div>
          <div className="font-light text-green-100">
            Showing{" "}
            {currentPageRange.start === currentPageRange.end
              ? currentPageRange.end
              : `${currentPageRange.start}-${currentPageRange.end}`}{" "}
            of {currentNewsList.length}
          </div>
        </div>
      </main>
      {footer && footer.enabled && footer.link ? (
        <CallToActionCard
          description={footer.description || ""}
          title={footer.title || ""}
          link={footer.link}
        />
      ) : null}
    </>
  );
}
